package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.BlockedUserDto
import fr.frux.shared.dto.BlockedUsersResponse
import fr.frux.shared.dto.CreateUserReportRequest
import fr.frux.shared.dto.UserProfileDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody

/**
 * Profil public d'un contributeur (#227) et gestes de l'ADR 0029 (#515). Le profil se lit sans
 * session. Avec une session, `blockedByMe` dit si l'appelant masque ce compte.
 */
interface UserApi {
    suspend fun getUserProfile(id: Long): UserProfileDto

    /** Masque les contributions de [id] pour l'appelant. 204 idempotent, 409 `TARGET_NOT_BLOCKABLE`
     *  (compte système, ADMIN ou review), 400 `SELF_BLOCK`, 404 `USER_NOT_FOUND`. */
    suspend fun blockUser(id: Long)

    /** Réaffiche [id]. 204 idempotent, sans contrôle d'existence. */
    suspend fun unblockUser(id: Long)

    /** Comptes masqués par l'appelant, le plus récent d'abord. */
    suspend fun getBlockedUsers(): List<BlockedUserDto>

    /** Signale [id] au back-office. [kind] parmi `UserReportLimits.USER_REPORT_KINDS`. 409
     *  `ALREADY_REPORTED` en doublon, 429 `REPORT_QUOTA_EXCEEDED` au-delà de 10 par 24 heures. */
    suspend fun reportUser(
        id: Long,
        kind: String,
        details: String? = null,
    )
}

@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class UserApiImpl(
    private val httpClient: HttpClient,
) : UserApi {
    override suspend fun getUserProfile(id: Long): UserProfileDto = httpClient.get("${ApiPaths.V1}/users/$id").body()

    override suspend fun blockUser(id: Long) {
        httpClient.put("${ApiPaths.V1}/users/$id/block")
    }

    override suspend fun unblockUser(id: Long) {
        httpClient.delete("${ApiPaths.V1}/users/$id/block")
    }

    override suspend fun getBlockedUsers(): List<BlockedUserDto> =
        httpClient.get("${ApiPaths.V1}/me/blocks").body<BlockedUsersResponse>().users

    override suspend fun reportUser(
        id: Long,
        kind: String,
        details: String?,
    ) {
        httpClient.post("${ApiPaths.V1}/users/$id/report") {
            setBody(CreateUserReportRequest(kind = kind, details = details))
        }
    }
}
